import React, { useState, useEffect, FC } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Row, Col, Button, Alert } from "reactstrap";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import { Icon } from "../../../components/Component";
import locationMasterApis from "../../../api/master/locationMaster";
import sitebreakdownMasterApis from "../../../api/master/sitebreakdownMaster";
import Swal from "sweetalert2";
import useSequence from "../../../hooks/useSequence";

interface SelectOptions {
  label: string;
  value: string | number;
}

const LocationMasterForm: FC<{
  id?: string | null;
  formAction?: string | null;
  toggleModelPopUp: () => void;
  refreshData?: () => void;
}> = ({ id, formAction, toggleModelPopUp, refreshData }) => {
  const [action, setAction] = useState(formAction);
  const [locationDetail, setLocationDetail] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<SelectOptions[]>([]);
  const [alertInfo, setAlertInfo] = useState({
    type: "",
    message: "",
    show: false,
  });

  const { hasSequence } = useSequence(163);
  const methods = useForm({ mode: "onChange" });
  const { handleSubmit, setValue, getValues, watch } = methods;
  const formValues = watch();

  const locationTypes = [
    { label: "Stage", value: "Stage" },
    { label: "External", value: "External" },
  ];

  const showAlertMessage = (type: string, message: string) => {
    setAlertInfo({ type, message, show: true });
  };

  const hideAlert = () => {
    setAlertInfo((prev) => ({ ...prev, show: false }));
  };

  const fetchSiteProjects = async () => {
    try {
      const response =
        await sitebreakdownMasterApis.fetchSiteBreakDownDropDown();
      if (response?.status) {
        const projList = response.data.map((proj: any) => ({
          label: `${proj.projectName} : ${proj.siteName}`,
          value: proj.id,
        }));
        setProjects(projList);
      }
    } catch (error) {
      console.error("Error fetching site projects:", error);
    }
  };

  const fetchLocationDetails = async (locationId: number) => {
    try {
      setLoading(true);
      const response = await locationMasterApis.fetchLocationById(locationId);
      if (response?.status) {
        const data = response.data;
        setValue(
          "siteStructureLocationSequenceId",
          data.siteStructureLocationSequenceId
        );
        setLocationDetail(data);
        setValue("siteStructureId", data.siteStructureId);
        setValue("locationName", data.locationName);
        setValue("description", data.description || "");
        setValue("type", data.type);
        setValue("rank", data.sequenceRank);
      } else {
        showAlertMessage("error", "Failed to load location details.");
      }
    } catch {
      showAlertMessage("error", "Error fetching location details.");
    } finally {
      setLoading(false);
    }
  };

  const hasFormChanges = () => {
    if (action !== "edit" || !id) return true;
    if (!locationDetail || Object.keys(locationDetail).length === 0)
      return true;

    const current = getValues();
    const currentValues = {
      locationName: current.locationName || "",
      description: current.description || "",
    };

    const originalValues = {
      locationName: locationDetail.locationName || "",
      description: locationDetail.description || "",
    };

    return (
      currentValues.locationName !== originalValues.locationName ||
      currentValues.description !== originalValues.description
    );
  };

  const onSubmit = async () => {
    try {
      setLoading(true);
      hideAlert();

      const current = getValues();
      let response;

      if (action === "edit" && id) {
        if (!hasFormChanges()) {
          showAlertMessage("error", "No Changes. No fields were modified.");
          setTimeout(() => hideAlert(), 1500);
          setLoading(false);
          return;
        }

        const payload = {
          locationName: current.locationName,
          description: current.description || "",
        };
        response = await locationMasterApis.updateLocationById(
          Number(id),
          payload
        );
      } else {
        const payload: Record<string, any> = {
          siteStructureId: Number(current.siteStructureId),
          locationName: current.locationName,
          type: current.type,
          sequenceRank: Number(current.rank),
          description: current.description || "",
        };
        if (!hasSequence) {
          payload.siteStructureLocationSequenceId =
            current.siteStructureLocationSequenceId;
        }
        response = await locationMasterApis.createLocation(payload);
      }

      if (response?.status) {
        // Close modal immediately
        toggleModelPopUp();
        refreshData?.();

        // Show SweetAlert after modal is closed
        setTimeout(() => {
          Swal.fire({
            title: "Success!",
            text:
              action === "edit"
                ? "Location has been updated"
                : "Location has been created",
            icon: "success",
            confirmButtonText: "OK",
          });
        }, 100);
      } else {
        const msg = response?.message || "Something went wrong!";
        showAlertMessage("error", msg);
        setTimeout(() => hideAlert(), 1500);
      }
    } catch (error: any) {
      const errMsg =
        error?.response?.data?.message ||
        error?.message ||
        "An error occurred while saving.";
      showAlertMessage("error", errMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSiteProjects();
    if (id && action === "edit") {
      fetchLocationDetails(Number(id));
    }
  }, [id, action]);

  useEffect(() => {
    return () => {
      hideAlert();
    };
  }, []);

  if (
    loading &&
    action === "edit" &&
    id &&
    (!locationDetail || Object.keys(locationDetail).length === 0)
  ) {
    return (
      <div className="text-center p-4">
        <Icon name="spinner" className="spin" /> Loading...
      </div>
    );
  }

  return (
    <>
      <h5 className="title">
        {action === "edit"
          ? `Edit ${locationDetail.locationName || "Location"}`
          : "Create New Location"}
      </h5>

      <Alert
        color={alertInfo.type === "error" ? "danger" : "success"}
        className="alert-icon"
        isOpen={alertInfo.show}
        style={{
          textAlign: "left",
          fontSize: "14px",
          marginBottom: "1rem",
          marginTop: "0.5rem",
        }}
      >
        <Icon
          name={alertInfo.type === "error" ? "alert-circle" : "check-circle"}
        />
        <span>{alertInfo.message}</span>
      </Alert>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            {!hasSequence && (
              <Col md={6} className="mt-2">
                <FormInput
                  name="siteStructureLocationSequenceId"
                  label="Location ID"
                  required={true}
                  type="text"
                  pattern={/^[a-zA-Z0-9./_-]+$/}
                  maxLength={30}
                  disabled={action === "edit"}
                  placeholder="Enter Location ID"
                />
              </Col>
            )}

            <Col md={6} className="mt-2">
              <FormSelect
                name="siteStructureId"
                label="Project"
                options={projects}
                required
                placeholder="Select Project"
                disabled={action === "edit"}
              />
            </Col>

            <Col md={6} className="mt-2">
              <FormSelect
                name="type"
                label="Type"
                options={locationTypes}
                required
                placeholder="Select Type"
                disabled={action === "edit"}
              />
            </Col>

            <Col md={6} className="mt-2">
              <FormInput
                name="locationName"
                label="Location Name"
                type="text"
                placeholder="Enter Location Name"
                required
                maxLength={100}
              />
            </Col>

            <Col md={6} className="mt-2">
              <FormSelect
                name="rank"
                label="Rank"
                options={Array.from({ length: 10 }, (_, i) => ({
                  label: (i + 1).toString(),
                  value: i + 1,
                }))}
                required
                placeholder="Select Rank"
                disabled={action === "edit"}
              />
            </Col>

            <Col md="12" className="mt-2">
              <FormInput
                name="description"
                label="Description"
                type="textarea"
                placeholder="Enter description (optional)"
              />
            </Col>
          </Row>

          <Row className="mt-4">
            <Col lg="12">
              <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                <li>
                  <Button
                    className="btn btn-primary btn-md"
                    type="submit"
                    color="primary"
                    disabled={loading}
                  >
                    <span>{action === "edit" ? "Update" : "Submit"}</span>
                  </Button>
                </li>
                <li>
                  <Button
                    color="light"
                    outline
                    onClick={(e) => {
                      e.preventDefault();
                      toggleModelPopUp();
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </li>
              </ul>
            </Col>
          </Row>
        </form>
      </FormProvider>
    </>
  );
};

export default LocationMasterForm;
